<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import { useCrmStore } from '../store/useCrmStore';
import KanbanColumn from '../components/KanbanColumn.vue';
import ServiceOrderModal from '../components/ServiceOrderModal.vue';
import ManageColumnsModal from '../components/ManageColumnsModal.vue';
import { extractApiErrorMessage } from '../utils/error';
import { resolveCrmCurrency, toCurrencyCents } from '../utils/currency';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { currentAccount } = useAccount();
const crmStore = useCrmStore();

const ALLOWED_PIPELINES = ['support', 'service_orders'];

const isModalOpen = ref(false);
const isManageColumnsModalOpen = ref(false);
const modalMode = ref('create');
const selectedOrder = ref(null);
const filters = reactive({
  emotion_label: '',
  satisfaction_score_min: '',
  satisfaction_score_max: '',
  value_min: '',
  value_max: '',
  difficulty_level: '',
  priority_level: '',
  due_date_state: '',
});

const confirmDialog = ref(null);
const dragSnapshot = ref(null);
const isDragging = ref(false);

const accountId = computed(() => {
  return route.params.accountId || window.location.pathname.split('/')[3];
});

const resolvePipeline = rawValue => {
  return ALLOWED_PIPELINES.includes(rawValue) ? rawValue : 'support';
};

const pipeline = computed(() => resolvePipeline(route.query.pipeline));
const isServiceOrdersPipeline = computed(
  () => pipeline.value === 'service_orders'
);
const crmCurrency = computed(() => resolveCrmCurrency(currentAccount.value));
const isSupportPipeline = computed(() => pipeline.value === 'support');
const translationScope = computed(() => {
  if (pipeline.value === 'support') {
    return 'CRM.PIPELINES.SUPPORT';
  }

  return 'CRM.PIPELINES.SERVICE_ORDERS';
});

const columns = computed(() => crmStore.getColumns);
const isFetching = computed(() => crmStore.getIsFetching);
const BOARD_COLUMN_MIN_WIDTH_REM = 18;
const BOARD_COLUMN_GAP_REM = 1;
const columnWidthStyle = computed(() => {
  const count = columns.value.length || 1;
  const totalGapRem = Math.max(count - 1, 0) * BOARD_COLUMN_GAP_REM;

  return {
    width: `max(${BOARD_COLUMN_MIN_WIDTH_REM}rem, calc((100% - ${totalGapRem}rem) / ${count}))`,
  };
});

const emotionFilterOptions = computed(() => [
  { value: '', label: t('CRM.BOARD_FILTERS.ALL') },
  {
    value: 'very_negative',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.VERY_NEGATIVE'),
  },
  { value: 'negative', label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.NEGATIVE') },
  { value: 'neutral', label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.NEUTRAL') },
  { value: 'positive', label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.POSITIVE') },
  {
    value: 'very_positive',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.VERY_POSITIVE'),
  },
  { value: 'unknown', label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.UNKNOWN') },
]);

const difficultyFilterOptions = computed(() => [
  { value: '', label: t('CRM.BOARD_FILTERS.ALL') },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
]);

const priorityFilterOptions = computed(() => [
  { value: '', label: t('CRM.BOARD_FILTERS.ALL') },
  { value: 'low', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.LOW') },
  { value: 'medium', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.MEDIUM') },
  { value: 'high', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.HIGH') },
  { value: 'urgent', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.URGENT') },
]);

const dueDateStateOptions = computed(() => [
  { value: '', label: t('CRM.BOARD_FILTERS.ALL') },
  { value: 'overdue', label: t('CRM.BOARD_FILTERS.DUE_DATE_OPTIONS.OVERDUE') },
  {
    value: 'due_today',
    label: t('CRM.BOARD_FILTERS.DUE_DATE_OPTIONS.DUE_TODAY'),
  },
  {
    value: 'upcoming',
    label: t('CRM.BOARD_FILTERS.DUE_DATE_OPTIONS.UPCOMING'),
  },
  {
    value: 'no_due_date',
    label: t('CRM.BOARD_FILTERS.DUE_DATE_OPTIONS.NO_DUE_DATE'),
  },
]);

const parseIntegerOrNull = value => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const cloneColumns = value => JSON.parse(JSON.stringify(value || []));

const resetFiltersForPipeline = () => {
  filters.emotion_label = '';
  filters.satisfaction_score_min = '';
  filters.satisfaction_score_max = '';
  filters.value_min = '';
  filters.value_max = '';
  filters.difficulty_level = '';
  filters.priority_level = '';
  filters.due_date_state = '';
};

const buildFiltersPayload = () => {
  if (isSupportPipeline.value) {
    return {
      emotion_label: filters.emotion_label,
      satisfaction_score_min: parseIntegerOrNull(
        filters.satisfaction_score_min
      ),
      satisfaction_score_max: parseIntegerOrNull(
        filters.satisfaction_score_max
      ),
    };
  }

  return {
    value_cents_min: toCurrencyCents(filters.value_min),
    value_cents_max: toCurrencyCents(filters.value_max),
    difficulty_level: parseIntegerOrNull(filters.difficulty_level),
    priority_level: filters.priority_level,
    due_date_state: filters.due_date_state,
  };
};

const syncPipelineInUrl = async value => {
  if (route.query.pipeline === value) {
    return;
  }

  await router.replace({
    name: route.name,
    params: route.params,
    query: {
      ...route.query,
      pipeline: value,
    },
  });
};

const fetchBoard = async () => {
  try {
    await crmStore.fetchKanbanData({
      accountId: accountId.value,
      pipeline: pipeline.value,
      filters: buildFiltersPayload(),
    });
  } catch (error) {
    useAlert(extractApiErrorMessage(error, t('CRM.ERRORS.FETCH_BOARD')));
  }
};

const applyFilters = () => {
  fetchBoard();
};

const clearFilters = () => {
  resetFiltersForPipeline();
  fetchBoard();
};

const openCreateModal = () => {
  selectedOrder.value = null;
  modalMode.value = 'create';
  isModalOpen.value = true;
};

const openEditModal = order => {
  if (isDragging.value) {
    return;
  }

  selectedOrder.value = order;
  modalMode.value = 'edit';
  isModalOpen.value = true;
};

const onModalSuccess = () => {
  fetchBoard();
};

const onManageColumnsSuccess = () => {
  fetchBoard();
};

const onOrderDelete = async order => {
  const ok = await confirmDialog.value?.showConfirmation();
  if (!ok) {
    return;
  }

  try {
    await crmStore.deleteServiceOrder({
      accountId: accountId.value,
      orderId: order.id,
      pipeline: pipeline.value,
    });
    useAlert(t(`${translationScope.value}.DELETE_SUCCESS`));
  } catch (error) {
    useAlert(extractApiErrorMessage(error, t('CRM.ERRORS.DELETE_ORDER')));
  }
};

const onDragStarted = () => {
  if (!isDragging.value) {
    dragSnapshot.value = cloneColumns(columns.value);
  }
  isDragging.value = true;
};

const onDragEnded = () => {
  setTimeout(() => {
    isDragging.value = false;
  }, 0);
};

const onOrderMoved = async ({ orderId, kanbanColumnId, position }) => {
  try {
    await crmStore.moveOrder({
      accountId: accountId.value,
      orderId,
      kanbanColumnId,
      position,
      pipeline: pipeline.value,
    });
    await fetchBoard();
  } catch (error) {
    if (dragSnapshot.value) {
      crmStore.replaceColumns(dragSnapshot.value);
    } else {
      await fetchBoard();
    }

    useAlert(extractApiErrorMessage(error, t('CRM.ERRORS.MOVE_ORDER')));
  } finally {
    dragSnapshot.value = null;
  }
};

watch(
  () => [route.params.accountId, pipeline.value],
  (_, previousValue) => {
    if (previousValue && previousValue[1] !== pipeline.value) {
      resetFiltersForPipeline();
    }

    fetchBoard();
  },
  { immediate: true }
);

watch(
  () => route.query.pipeline,
  value => {
    if (!ALLOWED_PIPELINES.includes(value)) {
      syncPipelineInUrl('support');
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex h-full w-full min-h-0 flex-col overflow-hidden px-4 pt-4 md:px-6 lg:px-8"
  >
    <div class="mb-4 shrink-0">
      <div
        class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
      >
        <div class="min-w-0">
          <div class="min-w-0">
            <h1 class="text-2xl font-bold text-n-slate-12 border-none m-0 p-0">
              {{ $t('CRM.TITLE') }}
            </h1>
            <p class="text-n-slate-10 text-sm">
              {{ $t('CRM.WELCOME') }}
            </p>
          </div>
        </div>

        <div
          class="flex w-full flex-wrap items-center justify-start gap-2 lg:w-auto lg:justify-end"
        >
          <NextButton
            v-if="isAdmin && isServiceOrdersPipeline"
            icon="i-lucide-columns-3"
            color="slate"
            variant="outline"
            size="sm"
            class="!whitespace-nowrap"
            :label="$t('CRM.COLUMN_MANAGER.OPEN_BUTTON')"
            @click="isManageColumnsModalOpen = true"
          />
          <NextButton
            icon="i-lucide-plus"
            size="sm"
            class="!whitespace-nowrap"
            :label="$t(`${translationScope}.NEW_ITEM_BUTTON`)"
            @click="openCreateModal"
          />
        </div>
      </div>
    </div>

    <div
      class="mb-4 shrink-0 rounded-xl border border-n-weak bg-n-alpha-1 p-3 md:p-4"
    >
      <div class="flex flex-col gap-3">
        <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
          {{ $t('CRM.BOARD_FILTERS.TITLE') }}
        </p>

        <div
          v-if="isSupportPipeline"
          class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
        >
          <div class="min-w-0 flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('CRM.BOARD_FILTERS.EMOTION_LABEL') }}
            </label>
            <select v-model="filters.emotion_label" class="!mb-0 text-sm">
              <option
                v-for="option in emotionFilterOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <Input
            v-model="filters.satisfaction_score_min"
            :label="$t('CRM.BOARD_FILTERS.SATISFACTION_MIN')"
            type="number"
            min="1"
            max="5"
            step="1"
            :placeholder="$t('CRM.BOARD_FILTERS.SATISFACTION_PLACEHOLDER')"
          />

          <Input
            v-model="filters.satisfaction_score_max"
            :label="$t('CRM.BOARD_FILTERS.SATISFACTION_MAX')"
            type="number"
            min="1"
            max="5"
            step="1"
            :placeholder="$t('CRM.BOARD_FILTERS.SATISFACTION_PLACEHOLDER')"
          />
        </div>

        <div
          v-else
          class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5"
        >
          <Input
            v-model="filters.value_min"
            :label="
              $t('CRM.BOARD_FILTERS.VALUE_MIN', { currency: crmCurrency })
            "
            type="number"
            min="0"
            step="0.01"
          />

          <Input
            v-model="filters.value_max"
            :label="
              $t('CRM.BOARD_FILTERS.VALUE_MAX', { currency: crmCurrency })
            "
            type="number"
            min="0"
            step="0.01"
          />

          <div class="min-w-0 flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('CRM.BOARD_FILTERS.DIFFICULTY') }}
            </label>
            <select v-model="filters.difficulty_level" class="!mb-0 text-sm">
              <option
                v-for="option in difficultyFilterOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="min-w-0 flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('CRM.BOARD_FILTERS.PRIORITY') }}
            </label>
            <select v-model="filters.priority_level" class="!mb-0 text-sm">
              <option
                v-for="option in priorityFilterOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="min-w-0 flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('CRM.BOARD_FILTERS.DUE_DATE_STATE') }}
            </label>
            <select v-model="filters.due_date_state" class="!mb-0 text-sm">
              <option
                v-for="option in dueDateStateOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div
          class="flex flex-wrap items-center justify-end gap-2 border-t border-n-weak pt-3"
        >
          <NextButton
            color="slate"
            variant="ghost"
            size="sm"
            :label="$t('CRM.BOARD_FILTERS.CLEAR')"
            @click="clearFilters"
          />
          <NextButton
            color="blue"
            size="sm"
            :label="$t('CRM.BOARD_FILTERS.APPLY')"
            @click="applyFilters"
          />
        </div>
      </div>
    </div>

    <div class="min-h-0 flex-1 overflow-hidden">
      <div class="h-full overflow-x-auto overflow-y-hidden pb-4">
        <div
          v-if="isFetching"
          class="flex h-full w-full items-center justify-center gap-2"
        >
          <woot-spinner size="" />
          <span class="text-n-slate-10">{{ $t('CRM.BOARD.LOADING') }}</span>
        </div>

        <div
          v-else
          class="flex h-full items-start"
          :class="
            columns.length === 0
              ? 'min-w-0 w-full'
              : 'w-full min-w-full gap-4 pr-2'
          "
        >
          <div
            v-if="columns.length === 0"
            class="flex h-32 w-full min-w-0 items-center justify-center rounded-xl border-2 border-dashed border-n-weak"
          >
            <p class="text-n-slate-10">
              {{ $t(`${translationScope}.EMPTY_STATE`) }}
            </p>
          </div>

          <KanbanColumn
            v-for="col in columns"
            :key="col.id"
            :column="col"
            :column-width-style="columnWidthStyle"
            :pipeline="pipeline"
            :crm-currency="crmCurrency"
            :can-delete-order="isAdmin"
            @drag-started="onDragStarted"
            @drag-ended="onDragEnded"
            @order-moved="onOrderMoved"
            @order-edit="openEditModal"
            @order-delete="onOrderDelete"
            @order-click="openEditModal"
          />
        </div>
      </div>
    </div>

    <ServiceOrderModal
      :show="isModalOpen"
      :mode="modalMode"
      :order="selectedOrder"
      :columns="columns"
      :pipeline="pipeline"
      :account-id="accountId"
      :currency="crmCurrency"
      @close="isModalOpen = false"
      @success="onModalSuccess"
    />

    <ManageColumnsModal
      :show="isManageColumnsModalOpen"
      :columns="columns"
      :account-id="accountId"
      @close="isManageColumnsModalOpen = false"
      @updated="onManageColumnsSuccess"
    />

    <ConfirmationModal
      ref="confirmDialog"
      :title="$t(`${translationScope}.DELETE_CONFIRM_TITLE`)"
      :description="$t(`${translationScope}.DELETE_CONFIRM_DESC`)"
      :confirm-label="$t('CRM.BOARD.DELETE_CONFIRM_YES')"
      :cancel-label="$t('CRM.BOARD.DELETE_CONFIRM_NO')"
    />
  </div>
</template>
